import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminDashboard } from "hooks/ovizia/useAdminDashboard";
import Icon from "components/common/Icon";
import { ADMIN_COLORS as C } from "../monitoring/adminColors";
import { fetchGenerationAttempts } from "hooks/ovizia/generationApi";
import { fetchTimelineEvents, TIMELINE_EVENT_TYPES } from "hooks/ovizia/timelineApi";


const STAT_TILES = [
  { key: "totalWebsites", label: "Total websites", icon: "app-window" },
  { key: "websitesCreatedToday", label: "Websites created today", icon: "calendar-plus" },
  { key: "websitesGenerating", label: "Currently generating", icon: "loader-2" },
  { key: "failedGenerations", label: "Failed generations", icon: "alert-triangle", tone: "red" },
  { key: "publishedWebsites", label: "Published websites", icon: "world" },
  { key: "failedPublishingJobs", label: "Failed publishing jobs", icon: "cloud-off", tone: "red" },
];

const RECENT_ITEM_LIMIT = 5;

const GENERATION_STATUS_STYLE = {
  QUEUED: { bg: C.bgSurface, fg: C.textMuted, label: "Queued" },
  RUNNING: { bg: C.blueBg, fg: C.blue, label: "Running" },
  COMPLETED: { bg: C.greenBg, fg: C.green, label: "Completed" },
  FAILED: { bg: C.redBg, fg: C.red, label: "Failed" },
};

const TIMELINE_TYPE_MAP = Object.fromEntries(TIMELINE_EVENT_TYPES.map((t) => [t.value, t]));

function StatTile({ label, icon, value, tone, loading }) {
  const fg = tone === "red" ? C.red : C.green;
  const bg = tone === "red" ? C.redBg : C.greenBg;
  return (
    <div
      style={{
        border: `1px solid ${C.border}`,
        borderRadius: 10,
        padding: "14px 16px",
        background: "#fff",
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div
          style={{
            width: 26,
            height: 26,
            borderRadius: 7,
            background: bg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon name={icon} size={13} style={{ color: fg }} />
        </div>
        <span style={{ fontSize: 11, fontWeight: 700, color: C.textMuted }}>
          {label}
        </span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color: C.text }}>
        {loading ? "—" : (value ?? 0).toLocaleString()}
      </div>
    </div>
  );
}

function AlertRow({ alert, onView }) {
  const critical = alert.severity === "critical";
  const fg = critical ? C.red : C.amber;
  const bg = critical ? C.redBg : C.amberBg;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        border: `1px solid ${critical ? "rgba(220,38,38,0.25)" : "rgba(180,83,9,0.25)"}`,
        background: bg,
        borderRadius: 8,
        padding: "10px 14px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        <Icon
          name={critical ? "alert-octagon" : "alert-triangle"}
          size={15}
          style={{ color: fg, flexShrink: 0 }}
        />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
            {alert.message}
            {typeof alert.count === "number" && (
              <span style={{ color: fg, fontWeight: 700 }}> · {alert.count}</span>
            )}
          </div>
        </div>
      </div>
      {alert.link && (
        <button
          onClick={() => onView(alert)}
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: fg,
            background: "#fff",
            border: `1px solid ${critical ? "rgba(220,38,38,0.35)" : "rgba(180,83,9,0.35)"}`,
            borderRadius: 6,
            padding: "5px 10px",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          View
        </button>
      )}
    </div>
  );
}

function PanelHeader({ title, onViewAll }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "14px 16px",
        borderBottom: `1px solid ${C.border}`,
      }}
    >
      <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{title}</span>
      <button
        onClick={onViewAll}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          fontSize: 12,
          fontWeight: 600,
          color: C.green,
          border: "none",
          background: "none",
          cursor: "pointer",
          padding: 0,
        }}
      >
        View all <Icon name="chevron-right" size={13} />
      </button>
    </div>
  );
}

function PanelEmpty({ label }) {
  return (
    <div style={{ padding: "24px 16px", textAlign: "center", color: C.textFaint, fontSize: 13 }}>
      {label}
    </div>
  );
}

function RecentAttemptRow({ attempt, onClick }) {
  const style = GENERATION_STATUS_STYLE[attempt.status] || GENERATION_STATUS_STYLE.QUEUED;
  return (
    <div
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 16px",
        borderTop: `1px solid ${C.border}`,
        cursor: "pointer",
      }}
    >
      <span
        style={{
          background: style.bg,
          color: style.fg,
          fontSize: 10,
          fontWeight: 700,
          padding: "2px 8px",
          borderRadius: 999,
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {style.label}
      </span>
      <span style={{ fontSize: 12, color: C.text, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {attempt.template || "—"}
      </span>
      <span style={{ fontSize: 11, color: C.textFaint, flexShrink: 0 }}>
        {attempt.startedAt ? new Date(attempt.startedAt).toLocaleString() : "Not started"}
      </span>
    </div>
  );
}

function RecentActivityRow({ event }) {
  const meta = TIMELINE_TYPE_MAP[event.type];
  const isError = (meta?.category ?? event.category) === "error";
  const fg = isError ? C.red : C.green;
  const bg = isError ? C.redBg : C.greenBg;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 16px",
        borderTop: `1px solid ${C.border}`,
      }}
    >
      <div
        style={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          background: bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon name={meta?.icon ?? "circle"} size={11} style={{ color: fg }} />
      </div>
      <span style={{ fontSize: 12, color: C.text, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {event.message}
      </span>
      <span style={{ fontSize: 11, color: C.textFaint, flexShrink: 0 }}>
        {new Date(event.createdAt).toLocaleString()}
      </span>
    </div>
  );
}

export default function OviziaDashboardPage() {
  const { stats, alerts, loading, error, refresh } = useAdminDashboard();
  const navigate = useNavigate();

  const [recentAttempts, setRecentAttempts] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [recentLoading, setRecentLoading] = useState(true);
  const [recentError, setRecentError] = useState(false);

  const loadRecent = useCallback(async () => {
    setRecentLoading(true);
    setRecentError(false);
    try {
      const [attempts, events] = await Promise.all([
        fetchGenerationAttempts({ size: RECENT_ITEM_LIMIT }),
        fetchTimelineEvents({ range: "7d" }),
      ]);
      setRecentAttempts(attempts.slice(0, RECENT_ITEM_LIMIT));
      setRecentEvents(events.slice(0, RECENT_ITEM_LIMIT));
    } catch {
      setRecentError(true);
    } finally {
      setRecentLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRecent();
  }, [loadRecent]);

  const handleRefreshAll = () => {
    refresh();
    loadRecent();
  };

  const handleViewAlert = (alert) => {
    if (alert.link) navigate(alert.link);
  };

  return (
    <div style={{ padding: "24px 28px", fontFamily: "Inter, system-ui, sans-serif" }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          marginBottom: 20,
        }}
      >
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: C.green, margin: 0 }}>
            Support dashboard
          </h1>
          <p style={{ fontSize: 13, color: C.textMuted, margin: "4px 0 0" }}>
            System health at a glance — jump into a section to dig in.
          </p>
        </div>
        <button
          onClick={handleRefreshAll}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            fontWeight: 600,
            color: C.textMuted,
            border: `1px solid ${C.border}`,
            borderRadius: 7,
            background: "#fff",
            padding: "7px 12px",
            cursor: "pointer",
          }}
        >
          <Icon name="refresh" size={13} /> Refresh
        </button>
      </div>

      {error && (
        <div
          style={{
            marginBottom: 16,
            fontSize: 12,
            color: C.red,
            background: C.redBg,
            border: "1px solid rgba(220,38,38,0.25)",
            borderRadius: 8,
            padding: "10px 14px",
          }}
        >
          Couldn't load dashboard data. Try refreshing.
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
          gap: 12,
          marginBottom: 24,
        }}
      >
        {STAT_TILES.map((tile) => (
          <StatTile
            key={tile.key}
            label={tile.label}
            icon={tile.icon}
            tone={tile.tone}
            value={stats?.[tile.key]}
            loading={loading}
          />
        ))}
      </div>

      <div style={{ marginBottom: 24 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: C.textFaint,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            marginBottom: 10,
          }}
        >
          Alerts
        </div>
        {loading ? (
          <div style={{ fontSize: 13, color: C.textFaint, padding: 14 }}>
            Loading alerts…
          </div>
        ) : alerts.length === 0 ? (
          <div
            style={{
              border: `1px solid ${C.border}`,
              borderRadius: 10,
              padding: "18px 16px",
              textAlign: "center",
              color: C.textFaint,
              fontSize: 13,
              background: "#fff",
            }}
          >
            No active alerts. Everything's healthy.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {alerts.map((alert) => (
              <AlertRow key={alert.id} alert={alert} onView={handleViewAlert} />
            ))}
          </div>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, background: "#fff", overflow: "hidden" }}>
          <PanelHeader title="Recent generation attempts" onViewAll={() => navigate("/ovizia/generation-monitoring")} />
          {recentLoading ? (
            <PanelEmpty label="Loading…" />
          ) : recentError ? (
            <PanelEmpty label="Couldn't load recent attempts." />
          ) : recentAttempts.length === 0 ? (
            <PanelEmpty label="No generation attempts yet." />
          ) : (
            recentAttempts.map((attempt) => (
              <RecentAttemptRow
                key={attempt.id}
                attempt={attempt}
                onClick={() => navigate("/ovizia/generation-monitoring")}
              />
            ))
          )}
        </div>

        <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, background: "#fff", overflow: "hidden" }}>
          <PanelHeader title="Recent activity" onViewAll={() => navigate("/ovizia/activity-timeline")} />
          {recentLoading ? (
            <PanelEmpty label="Loading…" />
          ) : recentError ? (
            <PanelEmpty label="Couldn't load recent activity." />
          ) : recentEvents.length === 0 ? (
            <PanelEmpty label="No activity in the last 7 days." />
          ) : (
            recentEvents.map((event) => <RecentActivityRow key={event.id} event={event} />)
          )}
        </div>
      </div>
    </div>
  );
}